import React from 'react';

import { dateRangeToFilterInput, filterInputToDateRange } from '@trqk/utils';
import { DateRangeSelectorDropdown } from '../..';
import { defaultFilterHeight, defaultFilterWidth } from '../constants';

interface DateRangeQueryFilterProps<
    FilterInput extends Record<
        string,
        Maybe<TableStringFilterInput | TableIntFilterInput>
    >,
> {
    /**
     * Default option for the date range filter
     */
    defaultOption: DropdownOption<MaybeEmptyDateRange>;
    /**
     * The field on the data to filter by
     */
    fieldName: keyof FilterInput;
    /**
     * The filter input for the AppSync query
     */
    filterInput: FilterInput;
    /**
     * Height for the DateRangeSelectorDropdown
     */
    height?: number;
    /**
     * Method to set the filter input for the AppSync query
     */
    setFilterInput: SetState<FilterInput>;
    /**
     * Width for the DateRangeSelectorDropdown
     */
    width?: number;
    /**
     * Label for selected date range
     */
    selectedLabel?: string;
    /**
     * Method to set label for selected date range
     */
    setSelectedLabel?: SetState<string>;
    /**
     * The object returned by the useModal hook
     */
    modal: ModalProviderValue;
}

/**
 * Returns a React component which renders a DateRangeSelectorDropdown that allows the user to
 * set a date range for a TableStringFilterInput.
 *
 * @remarks
 * This filter is implemented as an isolated component (outside of the react-table filtering API)
 * because it is used to set the AppSync query filter input, rather than filtering the data that is
 * already passed to a react-table instance.
 * @param props - {@link DateRangeQueryFilterProps}
 */

export function DateRangeQueryFilter<
    FilterInput extends Record<
        string,
        Maybe<TableStringFilterInput | TableIntFilterInput>
    >,
>({
    defaultOption,
    fieldName,
    filterInput,
    height = defaultFilterHeight,
    modal,
    width = defaultFilterWidth,
    setFilterInput,
    selectedLabel,
    setSelectedLabel,
}: DateRangeQueryFilterProps<FilterInput>): JSX.Element {
    return (
        <DateRangeSelectorDropdown
            borderConfig="solid.small"
            dateRange={filterInputToDateRange(filterInput?.[fieldName] ?? null)}
            initialOption={defaultOption}
            selectedLabel={selectedLabel}
            setSelectedLabel={setSelectedLabel}
            setDateRange={(nextValue: MaybeEmptyDateRange): void => {
                setFilterInput((prev: FilterInput): FilterInput => {
                    const nextFilterInput = dateRangeToFilterInput(nextValue);

                    return {
                        ...prev,
                        [fieldName]: nextFilterInput,
                    };
                });
            }}
            height={height}
            modal={modal}
            width={width}
        />
    );
}
